class AvpCartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.list = this.querySelector('[data-avp-recommendation-list]');
    this.statusElement = this.querySelector('[data-avp-recommendation-status]');
    this.requestSerial = 0;
    this.requestController = null;
    this.directAddHandles = new Set([
      'schmetterling-haarklammer-rosa-sasstie',
      'sasstie-lib-haarklammer-beere',
      'sasstie-lib-haarklammer-ocean',
      'sasstie-pure-dott-haarklammer-cremeweiss',
      'sasstie-dott-haarklammer-creme-mit-schwarzen-punkten',
      'sasstie-luv-haarklammer-blush',
      'sasstie-bloomie-haarklammer-macadamia',
      'sasstie-cheetah-haarklammer-leo-optik',
      'scrunchie-musselin-handgefarbt-bio-baumwolle-gots-rosebraun-apricot-weicher-haargummi-nachhaltig-geschenk-frau-muttertagsgeschenk'
    ]);
    this.blockedHandles = new Set([
      'sasstie-crush-caffe-haarklammer',
      'sasstie-joy-haarklammer-apricot-gesprenkelt',
      'blatt-schlusselanhanger-geblumt-kupfer-leder-geschenk-fur-frau-muttertag',
      'gold-glitzer-schlusselanhanger-schlusselband-karabinerhaken-geschenk-freundin-mama',
      'regenbogen-taschchen-gelbeutel-schlusselanhanger-set-geschenkset-bunt-braun-apricot-rose-punkte-boho-bio-valentinstag-krimskrams',
      'rechenkette-abc-name-regenbogen-madchen-flieder-mint-kupfer-erstes-zahlen-holz-schulkind-schulanfang-schultute-schlusselanhanger-geschenkset',
      'rechenkette-abc-name-regenbogen-jungen-senfgelb-blau-silber-erstes-zahlen-holz-schulkind-schulanfang-schultute-schlusselanhanger-set'
    ]);
    this.addEventListener('click', (event) => this.handleAction(event));
  }

  connectedCallback() {
    const cartNotification = this.closest('cart-notification');
    if (cartNotification && cartNotification.currentCart) {
      this.renderForCart(cartNotification.currentCart);
    }
  }

  disconnectedCallback() {
    if (this.requestController) this.requestController.abort();
  }

  async renderForCart(cart) {
    const serial = ++this.requestSerial;
    if (this.requestController) this.requestController.abort();
    this.requestController = new AbortController();
    const signal = this.requestController.signal;

    this.hidden = true;
    this.removeAttribute('aria-busy');
    this.list.innerHTML = '';
    this.setStatus('');

    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

    const cartIds = new Set(cart.items.map((item) => String(item.product_id)));
    const cartHandles = new Set(cart.items.map((item) => String(item.handle || '').toLowerCase()).filter(Boolean));
    const signals = this.getSignals(cart.items);
    const candidates = new Map();
    const productIds = Array.from(cartIds).slice(0, 6);

    try {
      this.setAttribute('aria-busy', 'true');

      const complementary = await Promise.allSettled(
        productIds.map((productId) => this.fetchRecommendations(productId, 'complementary', signal))
      );

      complementary.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.forEach((product, index) => {
          this.mergeCandidate(
            candidates,
            product,
            135 + Math.max(0, 6 - index) * 5 + this.contextScore(product, signals),
            'complementary',
            cartIds,
            cartHandles
          );
        });
      });

      const fallbackHandles = this.getFallbackHandles(signals)
        .filter((handle) => !cartHandles.has(handle))
        .slice(0, 8);

      const fallbacks = await Promise.allSettled(
        fallbackHandles.map((handle) => this.fetchProduct(handle, signal))
      );

      fallbacks.forEach((result, index) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        this.mergeCandidate(
          candidates,
          result.value,
          90 - index * 3 + this.contextScore(result.value, signals),
          'curated',
          cartIds,
          cartHandles
        );
      });

      if (candidates.size < 3) {
        const related = await Promise.allSettled(
          productIds.map((productId) => this.fetchRecommendations(productId, 'related', signal))
        );
        related.forEach((result) => {
          if (result.status !== 'fulfilled') return;
          result.value.forEach((product, index) => {
            this.mergeCandidate(
              candidates,
              product,
              55 + Math.max(0, 6 - index) * 3 + this.contextScore(product, signals),
              'related',
              cartIds,
              cartHandles
            );
          });
        });
      }

      if (serial !== this.requestSerial || signal.aborted) return;

      const ranked = Array.from(candidates.values())
        .sort((left, right) => right.score - left.score || right.support - left.support || left.product.price - right.product.price)
        .slice(0, 3);

      if (!ranked.length) return;

      this.list.innerHTML = ranked
        .map((candidate) => this.recommendationMarkup(candidate.product, signals))
        .join('');
      this.hidden = false;
    } catch (error) {
      if (error && error.name !== 'AbortError') this.hidden = true;
    } finally {
      if (serial === this.requestSerial) this.removeAttribute('aria-busy');
    }
  }

  async fetchRecommendations(productId, intent, signal) {
    const base = String(this.dataset.recommendationsUrl || '/recommendations/products').replace(/\.json$/, '');
    const url = `${base}.json?product_id=${encodeURIComponent(productId)}&limit=6&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
    if (!response.ok) return [];
    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  }

  async fetchProduct(handle, signal) {
    const root = this.dataset.productsRoot || '/products/';
    const response = await fetch(`${root}${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      signal
    });
    if (!response.ok) return null;
    return response.json();
  }

  mergeCandidate(map, rawProduct, score, source, cartIds, cartHandles) {
    const product = this.normaliseProduct(rawProduct);
    if (!product || !product.available) return;
    if (cartIds.has(product.id) || cartHandles.has(product.handle)) return;
    if (this.blockedHandles.has(product.handle)) return;

    const existing = map.get(product.handle);
    if (existing) {
      existing.score += score;
      existing.support += 1;
      if (source === 'complementary') existing.source = source;
      return;
    }

    map.set(product.handle, {
      product,
      score,
      source,
      support: 1
    });
  }

  normaliseProduct(product) {
    if (!product || !product.handle) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant.available !== false);
    if (product.available === false || !availableVariants.length) return null;

    const inventoryValues = variants
      .map((variant) => Number(variant.inventory_quantity))
      .filter((value) => Number.isFinite(value));
    if (inventoryValues.length && inventoryValues.reduce((sum, value) => sum + Math.max(0, value), 0) <= 1) {
      return null;
    }

    const firstVariant = availableVariants[0];
    const featuredImage =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image && product.featured_image.url
          ? product.featured_image.url
          : Array.isArray(product.images) && product.images.length
            ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src || product.images[0].url)
            : '';

    const price = this.toCents(
      product.price_min != null ? product.price_min : product.price != null ? product.price : firstVariant.price
    );
    const root = this.dataset.productsRoot || '/products/';
    const url = product.url || `${root}${product.handle}`;

    return {
      id: String(product.id),
      handle: String(product.handle).toLowerCase(),
      title: product.title || '',
      type: product.type || product.product_type || '',
      tags: product.tags || [],
      url,
      image: featuredImage || '',
      price,
      variantId: String(firstVariant.id),
      variantCount: variants.length,
      available: true
    };
  }

  toCents(value) {
    if (typeof value === 'number') return Math.round(value);
    const text = String(value == null ? 0 : value).trim().replace(',', '.');
    if (text.includes('.')) return Math.round(Number(text) * 100);
    return Math.round(Number(text) || 0);
  }

  getSignals(items) {
    const text = this.normaliseText(
      items
        .map((item) => [item.handle, item.product_title, item.product_type, item.vendor].filter(Boolean).join(' '))
        .join(' ')
    );
    const colorTokens = [
      'apricot', 'rose', 'rosa', 'flieder', 'lila', 'eukalyptus', 'oliv', 'salbei',
      'beige', 'braun', 'creme', 'schwarz', 'blau', 'mint', 'grun', 'gelb', 'senf',
      'beere', 'ocean', 'lemon', 'natur'
    ].filter((token) => text.includes(token));

    return {
      text,
      lowe: /lowe|loew|lowen/.test(text),
      regenbogen: /regenbogen|rainbow/.test(text),
      schule: /schultut|zuckertut|einschul|schulkind|kindergarten|kindergartentut|kita/.test(text),
      blueten: /blum|blut|bluete|flower|floral/.test(text),
      natur: /blatt|leaf|natur|wald|herbst|leinen|cord|eukalyptus|oliv|salbei/.test(text),
      mama: /mama|mutter|frau|geschenk/.test(text),
      haar: /haar|spange|haarklammer|scrunchie|schleife/.test(text),
      baby: /baby|geburt|kinderwagen|wagenkette|schnuller|mutterpass/.test(text),
      personalisiert: /personalis|mit name|initial|wunschname/.test(text),
      colors: colorTokens
    };
  }

  getFallbackHandles(signals) {
    const groups = {
      lowe: [
        'kugel-schlusselanhanger-mit-initiale-personalisiert-leder-schlussel-anhanger-kleines-geschenk-hakelkugel-holz-kinder-schulanfang',
        'sasstie-cheetah-haarklammer-leo-optik',
        'blatt-schlusselanhanger-cord-handgemacht-lederband-goldring-geschenk-fur-mama-freundin-beige-rose-flieder-eukalyptus'
      ],
      regenbogen: [
        'bunter-regenbogen-schlusselanhanger-personalisiert-geschenk-kinder-einschulung-glucksbringer',
        'regenbogen-schlusselanhanger-mit-name-personalisiert-glucksbringer-kleines-geschenk-bunt-kinder-schulanfang-madchen-schultute-schulkind',
        'personalisiertes-schulkind-armband-mit-namen-geschenk-zur-einschulung-oder-kindergartenstart-rose-und-mint-gold',
        'scrunchies-musselin-damen-madchen-bunte-haargummis-aus-baumwolle-colourblocking-pastell-geschenkidee-kinder'
      ],
      schule: [
        'personalisiertes-schulkind-armband-mit-namen-geschenk-zur-einschulung-oder-kindergartenstart-rose-und-mint-gold',
        'kugel-schlusselanhanger-mit-initiale-personalisiert-leder-schlussel-anhanger-kleines-geschenk-hakelkugel-holz-kinder-schulanfang',
        'bunter-regenbogen-schlusselanhanger-personalisiert-geschenk-kinder-einschulung-glucksbringer'
      ],
      blueten: [
        'sasstie-bloomie-haarklammer-macadamia',
        'blatt-schlusselanhanger-cord-handgemacht-lederband-goldring-geschenk-fur-mama-freundin-beige-rose-flieder-eukalyptus',
        'blatt-schlusselanhanger-leinen-gold-farbwahl-apricot-olivgrun-stoffblatt-kleines-geschenk-mama-muttertagsgeschenk'
      ],
      natur: [
        'blatt-schlusselanhanger-aus-baumwoll-leinen-schlichtes-design-lederband-schlusselring-gold-kupfer-oder-silber',
        'blatt-schlusselanhanger-leinen-gold-farbwahl-apricot-olivgrun-stoffblatt-kleines-geschenk-mama-muttertagsgeschenk',
        'blatt-schlusselanhanger-cord-handgemacht-lederband-goldring-geschenk-fur-mama-freundin-beige-rose-flieder-eukalyptus'
      ],
      mama: [
        'blatt-schlusselanhanger-aus-baumwoll-leinen-schlichtes-design-lederband-schlusselring-gold-kupfer-oder-silber',
        'scrunchie-musselin-handgefarbt-bio-baumwolle-gots-rosebraun-apricot-weicher-haargummi-nachhaltig-geschenk-frau-muttertagsgeschenk',
        'suncatcher-fensterdeko-modern-kristall-anhanger-mit-naturstein-messing-minimalistisch-edel-bergkristall-dalmatiner-gelbe-jade'
      ],
      haar: [
        'schmetterling-haarklammer-rosa-sasstie',
        'sasstie-lib-haarklammer-beere',
        'sasstie-lib-haarklammer-ocean',
        'sasstie-pure-dott-haarklammer-cremeweiss',
        'sasstie-dott-haarklammer-creme-mit-schwarzen-punkten',
        'sasstie-luv-haarklammer-blush'
      ]
    };

    const handles = [];
    const append = (name) => {
      groups[name].forEach((handle) => {
        if (!handles.includes(handle)) handles.push(handle);
      });
    };

    if (signals.lowe) append('lowe');
    if (signals.regenbogen) append('regenbogen');
    if (signals.blueten) append('blueten');
    if (signals.schule) append('schule');
    if (signals.natur) append('natur');
    if (signals.mama) append('mama');
    if (signals.haar) append('haar');

    return handles;
  }

  contextScore(product, signals) {
    const text = this.normaliseText(
      [product.handle, product.title, product.type, Array.isArray(product.tags) ? product.tags.join(' ') : product.tags]
        .filter(Boolean)
        .join(' ')
    );
    let score = 0;

    if (signals.lowe) {
      if (/lowe|loew|lowen/.test(text)) score += 85;
      else if (/initial|cheetah|cord/.test(text)) score += 28;
    }
    if (signals.regenbogen && /regenbogen|rainbow/.test(text)) score += 65;
    if (signals.schule && /schul|einschul|kindergarten|initial|armband|regenbogen/.test(text)) score += 48;
    if (signals.blueten && /blum|blut|bloom|floral|blatt/.test(text)) score += 55;
    if (signals.natur && /blatt|leinen|cord|natur|eukalyptus|oliv|salbei/.test(text)) score += 48;
    if (signals.mama && /blatt|scrunchie|suncatcher|mama|mutter|frau/.test(text)) score += 36;
    if (signals.haar && /haar|spange|haarklammer|scrunchie|sasstie/.test(text)) score += 45;
    if (signals.personalisiert && /initial|personalis|mit-name|mit_name/.test(text)) score += 20;

    const sharedColors = signals.colors.filter((color) => text.includes(color)).length;
    score += Math.min(30, sharedColors * 15);
    return score;
  }

  recommendationMarkup(product, signals) {
    const directAdd = product.variantCount === 1 && this.directAddHandles.has(product.handle);
    const imageUrl = this.imageUrl(product.image, 260);
    const image = imageUrl
      ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="260" height="260">`
      : '<span class="avp-cart-recommendation__placeholder" aria-hidden="true"></span>';
    const reason = this.reasonFor(product, signals);
    const action = directAdd
      ? `<button type="button" class="avp-cart-recommendation__action" data-avp-recommendation-add data-variant-id="${this.escapeHtml(product.variantId)}" aria-label="${this.escapeHtml(product.title)} in den Warenkorb legen">
          <span class="avp-cart-recommendation__action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M3 4h2l1.7 10.1a2 2 0 0 0 2 1.7h7.8a2 2 0 0 0 1.9-1.4L21 7H6.1M10 20h.01M17 20h.01M14 9v4M12 11h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span>Hinzufügen</span>
        </button>`
      : `<a class="avp-cart-recommendation__action avp-cart-recommendation__action--choose" href="${this.escapeHtml(product.url)}" aria-label="${this.escapeHtml(product.title)} auswählen">
          <span class="avp-cart-recommendation__action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M10 14v6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
          </span>
          <span>Auswählen</span>
        </a>`;

    return `<article class="avp-cart-recommendation" role="listitem" data-product-handle="${this.escapeHtml(product.handle)}">
      <a class="avp-cart-recommendation__image" href="${this.escapeHtml(product.url)}">${image}</a>
      <div class="avp-cart-recommendation__content">
        <span class="avp-cart-recommendation__reason">${this.escapeHtml(reason)}</span>
        <a class="avp-cart-recommendation__title" href="${this.escapeHtml(product.url)}">${this.escapeHtml(product.title)}</a>
        <span class="avp-cart-recommendation__price">${this.formatMoney(product.price)}</span>
        ${action}
      </div>
    </article>`;
  }

  reasonFor(product, signals) {
    const text = this.normaliseText(product.handle + ' ' + product.title);
    if (signals.regenbogen && /regenbogen|rainbow/.test(text)) return 'Passt zur Regenbogenwelt';
    if (signals.schule && /schul|einschul|kindergarten|initial|armband/.test(text)) return 'Für Kindergarten & Schule';
    if (signals.blueten && /blum|blut|bloom|floral/.test(text)) return 'Passend zur Blütenwelt';
    if (signals.natur && /blatt|leinen|cord|eukalyptus|oliv|salbei/.test(text)) return 'In passenden Naturtönen';
    if (signals.lowe && /initial/.test(text)) return 'Kleine persönliche Ergänzung';
    if (signals.lowe && /cheetah|cord/.test(text)) return 'Passend zur warmen Farbwelt';
    if (signals.haar && /haar|spange|scrunchie|sasstie/.test(text)) return 'Als kleines passendes Extra';
    if (signals.mama) return 'Schön als kleine Ergänzung';
    return 'Sorgfältig dazu gewählt';
  }

  async handleAction(event) {
    const button = event.target.closest('[data-avp-recommendation-add]');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.setStatus('');

    try {
      const endpoint = typeof routes !== 'undefined' && routes.cart_add_url ? routes.cart_add_url : '/cart/add';
      const response = await fetch(`${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }]
        })
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          result && (result.description || result.message)
            ? String(result.description || result.message)
            : 'Das Produkt konnte gerade nicht hinzugefügt werden.';
        this.setStatus(message);
        return;
      }

      const cartNotification = this.closest('cart-notification');
      if (cartNotification && typeof cartNotification.refreshCart === 'function') {
        await cartNotification.refreshCart();
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      this.setStatus('Das Produkt konnte gerade nicht hinzugefügt werden. Bitte versuche es erneut.');
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  setStatus(message) {
    if (!this.statusElement) return;
    this.statusElement.textContent = message;
    this.statusElement.hidden = !message;
  }

  imageUrl(source, width) {
    if (!source) return '';
    const separator = source.includes('?') ? '&' : '?';
    return `${source}${separator}width=${width}`;
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency
    }).format((Number(cents) || 0) / 100);
  }

  normaliseText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }
}

if (!customElements.get('avp-cart-recommendations')) {
  customElements.define('avp-cart-recommendations', AvpCartRecommendations);
}
